import { CookieKey, NotiButtonState, StatusCodes } from '@/constants';
import { subscribeAllShipments, subscribeSingleShipment } from '@/fetchers';
import { useGlobalState } from '@/hooks';
import { LoginState } from '@/interfaces';
import { type ITrackingDetailsContext, TrackingDetailsContext } from '@/modules/pmt/components/TrackingDetails/context';
import { UNKNOWN_CARRIER_ID } from '@/modules/pmt/constants';
import { useMyParcelsContext, useShipmentDetailsContext } from '@/modules/pmt/context';
import { PmtSignUpStep, ShipmentSearchStatus, ShipmentSubscribeState } from '@/modules/pmt/interfaces';
import { getShipmentSearchStatus } from '@/modules/pmt/utils';
import { gtmClickNotified } from '@/utils';
import { useStatsigClient } from '@statsig/react-bindings';
import Cookies from 'js-cookie';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

type UseSubscribeNoti = () => {
  loginState: LoginState | undefined;
  notiButtonState: NotiButtonState | undefined;
  handleNotiButtonClick: () => void;
};
/*
 * SUBSCRIBE NOTIFICATION SECTION - shows for loading/not found/found shipments
 * UC1: isUnverified = !isLoggedIn && !!userInfo?.email => shows UI, click to view Popup
 * UC2: not isLoggedIn => shows UI, click to view Popup
 * UC3: isLoggedIn => 2 cases
 *   a. defaultOptIn = false
 *      => shows UI for all shipments, click to subscribe
 *   b. defaultOptIn = true & noti_{shipment_id} = false | undefined
 *      => shows UI for single shipment, click to subscribe
 */

export const useSubscribeNoti: UseSubscribeNoti = () => {
  const { setShowPMTPopUp, setCurrentPMTSignUpStep, setIsFromGetNotifiedBtn } = useGlobalState();
  const { currentShipment } = useShipmentDetailsContext();
  const currentShipmentData = currentShipment?.data;
  const { notificationTranslation } = useMyParcelsContext();

  // TODO: parcel_id/carrier_id are no longer being used, need to check later to see if there's redundant code
  const {
    subAllApiState,
    setSubAllApiState,
    notiButtonStateByParcel,
    setNotiButtonStateByParcel: _setNotiButtonStateByParcel,
  } = useContext<ITrackingDetailsContext>(TrackingDetailsContext);
  const { isLoggedIn, userInfo, setUserInfo, pmtCurrentLanguageCode } = useGlobalState();

  const [apiSingleDone, setApiSingleDone] = useState(false);
  const { logEvent } = useStatsigClient();

  // * Constants
  const { defaultOptIn } = userInfo || {};

  const currentParcel = useMemo(() => {
    if (currentShipment) {
      return {
        ...currentShipment.data,
        carrierId: currentShipment.data.carrierId || UNKNOWN_CARRIER_ID,
      };
    }
  }, [currentShipment]);

  const currentParcelExists = currentParcel?.parcel?.parcelId;

  const notiButtonState = useMemo(() => {
    if (!currentParcelExists || !currentParcel.carrierId) {
      return;
    }

    return notiButtonStateByParcel[currentParcel.parcel?.parcelId]?.[currentParcel.carrierId];
  }, [currentParcelExists, notiButtonStateByParcel, currentParcel?.carrierId, currentParcel?.parcel?.parcelId]);

  const isNotiButtonStateNotInProcess = useMemo(
    () =>
      ![NotiButtonState.Processing, NotiButtonState.Subscribed, NotiButtonState.Hidden].includes(
        notiButtonState as NotiButtonState,
      ),
    [notiButtonState],
  );

  const loginState = useMemo(() => {
    if (!isLoggedIn && !!userInfo?.email) {
      return LoginState.Unverified;
    }

    if (!isLoggedIn) {
      return LoginState.NotLoggedIn;
    }

    return LoginState.Login;
  }, [isLoggedIn, userInfo]);

  // * Functions
  const updateNotiButtonStateByParcel = useCallback(
    (state: NotiButtonState) => {
      if (!currentParcelExists) {
        return;
      }
      _setNotiButtonStateByParcel((prevState) => ({
        ...prevState,
        [currentParcel.parcel?.parcelId]: {
          [currentParcel.carrierId]: state,
        },
      }));
    },
    [currentParcel],
  );

  // * SUBSINGLE: subscribe single shipment API call
  const handleSubscribeSingleShipment = async (accessToken: string) => {
    if (!currentParcelExists || !currentShipment?.data.firestoreDocId) {
      console.error('Could not subscribe to shipment: shipmentDocId does not exist');
      return;
    }

    try {
      const response = await subscribeSingleShipment({
        shipmentDocId: currentShipment.data.firestoreDocId,
        accessToken,
      });
      switch (true) {
        // biome-ignore lint/suspicious/noDoubleEquals: <explanation>
        case response?.status == StatusCodes.SUCCESS_200:
          handleSubscribeStatesWithTimeout();
          return;
        default: {
          setApiSingleDone(true);
        }
      }
    } catch {
      updateNotiButtonStateByParcel(NotiButtonState.Default);
    }
  };

  // * SUBALL: subscribe all shipments API call
  const handleSubscribeAllShipments = async (accessToken: string) => {
    try {
      setSubAllApiState(true);
      const response = await subscribeAllShipments({
        language: pmtCurrentLanguageCode,
        accessToken,
      });

      // biome-ignore lint/suspicious/noDoubleEquals: <explanation>
      if (response?.status != StatusCodes.SUCCESS_200) {
        updateNotiButtonStateByParcel(NotiButtonState.Default);
        return;
      }

      // * default_opt_in = true <= BE changes this
      // * all shipments' noti_{shipment_id} = true <= BE changes this on firebase
      userInfo &&
        setUserInfo({
          ...userInfo,
          defaultOptIn: true, // set state for current session on FE side, actual change is on BE side
        });
      handleSubscribeStatesWithTimeout();
      setSubAllApiState(false);
    } catch {
      updateNotiButtonStateByParcel(NotiButtonState.Default);
    }
  };

  // * change animation through notiButtonState state
  const handleSubscribeStatesWithTimeout = () => {
    // For adding a CheckIcon
    updateNotiButtonStateByParcel(NotiButtonState.Subscribed);

    setTimeout(() => {
      // For fade effect: changing the component styling to hidden
      updateNotiButtonStateByParcel(NotiButtonState.Hidden);

      setTimeout(() => {
        // For deleting the component
        updateNotiButtonStateByParcel(NotiButtonState.None);
      }, 400);
    }, 2000);
  };

  // * handle noti button click: call API or show popup
  const handleNotiButtonClick = async () => {
    if (!currentParcelExists || (notiButtonState && notiButtonState !== NotiButtonState.Default)) {
      return;
    }

    setIsFromGetNotifiedBtn(true);
    loginState && gtmClickNotified(loginState, notificationTranslation.identifier);
    loginState &&
      logEvent('click_notified', undefined, { loginState, variant: notificationTranslation.identifier ?? '' });

    // * UC1: isUnverified and UC2: not isLoggedIn => click to show Popup
    if (!isLoggedIn) {
      setCurrentPMTSignUpStep(PmtSignUpStep.SignUp);
      setShowPMTPopUp(true);
      return;
    }

    // For adding a SpinnerImage, Spinner might load below 1s
    updateNotiButtonStateByParcel(NotiButtonState.Processing);

    // * UC3: isLoggedIn => 2 cases
    // * defaultOptIn = false => sub all
    // * defaultOptIn = true & noti_{shipment_id} = false | undefined => sub single
    const accessToken = Cookies.get(CookieKey.AccessToken);

    if (defaultOptIn) {
      await handleSubscribeSingleShipment(accessToken);
    } else if (!subAllApiState) {
      // just in case there is a delay in UI
      await handleSubscribeAllShipments(accessToken);
    }
  };

  const getNotiButtonStateFromSubscribeState = useCallback(
    (checkInProcessState = false) => {
      if (!currentParcelExists) {
        return;
      }

      const isSubscribed =
        currentShipment &&
        [ShipmentSubscribeState.SubscribeByDefault, ShipmentSubscribeState.SubscribeByPMT].includes(
          currentShipment.data.subscribeState,
        );

      // Even if notiButtonState is in animation mode (subscribed, hidden, none), return the state
      if (!checkInProcessState) return isSubscribed ? NotiButtonState.None : NotiButtonState.Default;

      // If notiButtonState is in animation mode (subscribed, hidden, none), do nothing
      if (!notiButtonState || isNotiButtonStateNotInProcess) {
        return isSubscribed ? NotiButtonState.None : NotiButtonState.Default;
      }
    },
    [currentParcelExists, currentShipment, isNotiButtonStateNotInProcess, notiButtonState],
  );

  // * handle for `subscribe_state: undefined` changes to `subscribe_state: subscribed_by_pmt` due to BE handling of:
  // * 1st step: set parcel-related data to firestore <= can click button at this step, until 2nd step happens
  // * 2nd step: set notification data to firestore
  useEffect(() => {
    if (!apiSingleDone) return;
    const _notiButtonState = getNotiButtonStateFromSubscribeState();
    _notiButtonState && updateNotiButtonStateByParcel(_notiButtonState);
    setApiSingleDone(false);
  }, [apiSingleDone]);

  // * handle for normal subscribe_state changes
  useEffect(() => {
    const searchStatus = getShipmentSearchStatus(currentShipment?.data);

    const isFinding = [ShipmentSearchStatus.Pending, ShipmentSearchStatus.ScrapingWithCarrier].includes(searchStatus);

    switch (true) {
      case subAllApiState && isNotiButtonStateNotInProcess:
      case currentShipmentData && isFinding && isNotiButtonStateNotInProcess:
        updateNotiButtonStateByParcel(NotiButtonState.None);
        return;
      case !currentShipmentData || !isLoggedIn:
      case !defaultOptIn && isNotiButtonStateNotInProcess:
        // * State for default UC3a, when notiButtonState changes (omit animation mode)
        updateNotiButtonStateByParcel(NotiButtonState.Default);
        return;
    }

    // * UC3b: defaultOptIn = true & noti_{shipment_id} = false | undefined
    const _notiButtonState = getNotiButtonStateFromSubscribeState(true);
    _notiButtonState && updateNotiButtonStateByParcel(_notiButtonState);
  }, [
    isLoggedIn,
    defaultOptIn,
    subAllApiState,
    currentShipment?.data,
    isNotiButtonStateNotInProcess,
    currentShipmentData,
  ]);

  return {
    loginState,
    notiButtonState,
    handleNotiButtonClick,
  };
};
